import React, { useState, useEffect } from 'react';
import { COLORS } from '../config/constants';
import { reportsService, projectService } from '../services/api.service';
import { BarChart, Bar, PieChart, Pie, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

const Reports = ({ user }) => {
  const [activeTab, setActiveTab] = useState('stock');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Filters
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    project: '',
    operator: '',
    location: '',
    source_location: '',
    destination: ''
  });

  // Report data
  const [stockData, setStockData] = useState(null);
  const [productionData, setProductionData] = useState(null);
  const [shipmentData, setShipmentData] = useState(null);
  const [generalStats, setGeneralStats] = useState(null);

  const [projects, setProjects] = useState([]);

  const CHART_COLORS = ['#E40A04', '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  useEffect(() => {
    loadProjects();
    loadReports();
  }, []);

  const loadProjects = async () => {
    try {
      const data = await projectService.getProjects();
      setProjects(data);
    } catch (error) {
      console.error('Projeler yüklenemedi:', error);
    }
  };

  const loadReports = async () => {
    setLoading(true);
    try {
      const [stock, production, shipment, general] = await Promise.all([
        reportsService.getStockSummary(filters),
        reportsService.getProductionPerformance(filters),
        reportsService.getShipmentAnalysis(filters),
        reportsService.getGeneralStatistics(filters)
      ]);

      setStockData(stock);
      setProductionData(production);
      setShipmentData(shipment);
      setGeneralStats(general);
    } catch (error) {
      setMessage({ type: 'error', text: 'Raporlar yüklenemedi!' });
      console.error('Rapor hatası:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    loadReports();
  };

  const clearFilters = () => {
    setFilters({
      date_from: '',
      date_to: '',
      project: '',
      operator: '',
      location: '',
      source_location: '',
      destination: ''
    });
    setTimeout(() => loadReports(), 100);
  };

  // Quick date filters
  const setQuickDate = (type) => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    
    switch(type) {
      case 'today':
        setFilters(prev => ({
          ...prev,
          date_from: `${year}-${month}-${day}`,
          date_to: `${year}-${month}-${day}`
        }));
        break;
      case 'week':
        const weekAgo = new Date(today.setDate(today.getDate() - 7));
        setFilters(prev => ({
          ...prev,
          date_from: weekAgo.toISOString().split('T')[0],
          date_to: `${year}-${month}-${day}`
        }));
        break;
      case 'month':
        setFilters(prev => ({
          ...prev,
          date_from: `${year}-${month}-01`,
          date_to: `${year}-${month}-${day}`
        }));
        break;
      default:
        break;
    }
    setTimeout(() => loadReports(), 100);
  };

  const renderStockReport = () => {
    if (!stockData) return <p>Veri yükleniyor...</p>;

    // Prepare chart data
    const locationData = Object.entries(stockData.by_location || {}).map(([name, data]) => ({
      name,
      Giriş: data.in,
      Çıkış: data.out,
      Mevcut: data.current
    }));

    const materialData = Object.entries(stockData.by_material || {}).map(([name, data]) => ({
      name: name.substring(0, 20),
      value: data.current
    }));

    return (
      <div>
        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Toplam Giriş</p>
            <h2 style={{ color: COLORS.primary, margin: 0 }}>{stockData.summary.total_in.toFixed(2)} kg</h2>
          </div>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Toplam Çıkış</p>
            <h2 style={{ color: '#f59e0b', margin: 0 }}>{stockData.summary.total_out.toFixed(2)} kg</h2>
          </div>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Mevcut Stok</p>
            <h2 style={{ color: '#10b981', margin: 0 }}>{stockData.summary.current_stock.toFixed(2)} kg</h2>
          </div>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Toplam Kayıt</p>
            <h2 style={{ color: '#6366f1', margin: 0 }}>{stockData.summary.record_count}</h2>
          </div>
        </div>

        {/* Charts */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <h3 style={{ marginTop: 0 }}>Lokasyon Bazında Stok</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={locationData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="Giriş" fill="#10b981" />
                <Bar dataKey="Çıkış" fill="#ef4444" />
                <Bar dataKey="Mevcut" fill={COLORS.primary} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <h3 style={{ marginTop: 0 }}>Malzeme Dağılımı</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={materialData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                  {materialData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  const renderProductionReport = () => {
    if (!productionData) return <p>Veri yükleniyor...</p>;

    const operatorData = Object.entries(productionData.by_operator || {}).map(([name, data]) => ({
      name,
      Miktar: data.quantity,
      'Ağırlık (kg)': data.weight
    }));

    const projectData = Object.entries(productionData.by_project || {}).map(([name, data]) => ({
      name: name.substring(0, 15),
      value: data.weight
    }));

    return (
      <div>
        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Toplam Miktar</p>
            <h2 style={{ color: COLORS.primary, margin: 0 }}>{productionData.summary.total_quantity}</h2>
          </div>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Toplam Ağırlık</p>
            <h2 style={{ color: '#10b981', margin: 0 }}>{productionData.summary.total_weight.toFixed(2)} kg</h2>
          </div>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Ortalama Ağırlık</p>
            <h2 style={{ color: '#f59e0b', margin: 0 }}>{productionData.summary.avg_weight_per_unit.toFixed(2)} kg</h2>
          </div>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Toplam Kayıt</p>
            <h2 style={{ color: '#6366f1', margin: 0 }}>{productionData.summary.record_count}</h2>
          </div>
        </div>

        {/* Charts */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <h3 style={{ marginTop: 0 }}>Operatör Performansı</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={operatorData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="Miktar" fill="#6366f1" />
                <Bar dataKey="Ağırlık (kg)" fill={COLORS.primary} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <h3 style={{ marginTop: 0 }}>Proje Bazında Dağılım</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={projectData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                  {projectData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  const renderShipmentReport = () => {
    if (!shipmentData) return <p>Veri yükleniyor...</p>;

    const destinationData = Object.entries(shipmentData.by_destination || {}).map(([name, data]) => ({
      name: name.substring(0, 20),
      Miktar: data.quantity,
      'Ağırlık (kg)': data.weight
    }));

    const sourceData = Object.entries(shipmentData.by_source || {}).map(([name, data]) => ({
      name: name.substring(0, 15),
      value: data.weight
    }));

    return (
      <div>
        {/* Summary Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Toplam Miktar</p>
            <h2 style={{ color: COLORS.primary, margin: 0 }}>{shipmentData.summary.total_quantity}</h2>
          </div>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Toplam Ağırlık</p>
            <h2 style={{ color: '#10b981', margin: 0 }}>{shipmentData.summary.total_weight.toFixed(2)} kg</h2>
          </div>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Toplam Kayıt</p>
            <h2 style={{ color: '#6366f1', margin: 0 }}>{shipmentData.summary.record_count}</h2>
          </div>
        </div>

        {/* Charts */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <h3 style={{ marginTop: 0 }}>Hedef Bazında Sevkiyat</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={destinationData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="Miktar" fill="#0088FE" />
                <Bar dataKey="Ağırlık (kg)" fill={COLORS.primary} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <h3 style={{ marginTop: 0 }}>Kaynak Lokasyon Dağılımı</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={sourceData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                  {sourceData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  const renderGeneralStats = () => {
    if (!generalStats) return <p>Veri yükleniyor...</p>;

    return (
      <div>
        {/* Overview Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Toplam Proje</p>
            <h2 style={{ color: COLORS.primary, margin: 0 }}>{generalStats.overview.total_projects}</h2>
          </div>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Aktif Proje</p>
            <h2 style={{ color: '#10b981', margin: 0 }}>{generalStats.overview.active_projects}</h2>
          </div>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Stok Kayıtları</p>
            <h2 style={{ color: '#6366f1', margin: 0 }}>{generalStats.overview.total_stock_records}</h2>
          </div>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Üretim Kayıtları</p>
            <h2 style={{ color: '#f59e0b', margin: 0 }}>{generalStats.overview.total_production_records}</h2>
          </div>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.5rem' }}>Sevkiyat Kayıtları</p>
            <h2 style={{ color: '#0088FE', margin: 0 }}>{generalStats.overview.total_shipment_records}</h2>
          </div>
        </div>

        {/* Module Statistics */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem', marginBottom: '2rem' }}>
          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <h3 style={{ color: COLORS.primary, marginTop: 0 }}>📦 Stok</h3>
            <div style={{ marginTop: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span>Giriş:</span>
                <strong>{generalStats.stock.total_in.toFixed(2)} kg</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span>Çıkış:</span>
                <strong>{generalStats.stock.total_out.toFixed(2)} kg</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', background: '#f3f4f6', borderRadius: '6px' }}>
                <span>Mevcut:</span>
                <strong style={{ color: '#10b981' }}>{generalStats.stock.current.toFixed(2)} kg</strong>
              </div>
            </div>
          </div>

          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <h3 style={{ color: '#f59e0b', marginTop: 0 }}>🏭 Üretim</h3>
            <div style={{ marginTop: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span>Miktar:</span>
                <strong>{generalStats.production.total_quantity} adet</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', background: '#f3f4f6', borderRadius: '6px' }}>
                <span>Ağırlık:</span>
                <strong style={{ color: '#f59e0b' }}>{generalStats.production.total_weight.toFixed(2)} kg</strong>
              </div>
            </div>
          </div>

          <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <h3 style={{ color: '#0088FE', marginTop: 0 }}>🚚 Sevkiyat</h3>
            <div style={{ marginTop: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span>Miktar:</span>
                <strong>{generalStats.shipment.total_quantity} adet</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem', background: '#f3f4f6', borderRadius: '6px' }}>
                <span>Ağırlık:</span>
                <strong style={{ color: '#0088FE' }}>{generalStats.shipment.total_weight.toFixed(2)} kg</strong>
              </div>
            </div>
          </div>
        </div>

        {/* Efficiency Metrics */}
        <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <h3 style={{ marginTop: 0 }}>📈 Verimlilik Metrikleri</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginTop: '1.5rem' }}>
            <div>
              <p style={{ color: '#666', marginBottom: '0.5rem' }}>Üretim/Sevkiyat Oranı</p>
              <div style={{ background: '#f3f4f6', borderRadius: '8px', padding: '1rem' }}>
                <h2 style={{ color: COLORS.primary, margin: 0 }}>{generalStats.efficiency.production_to_shipment_ratio.toFixed(2)}%</h2>
              </div>
            </div>
            <div>
              <p style={{ color: '#666', marginBottom: '0.5rem' }}>Stok Devir Hızı</p>
              <div style={{ background: '#f3f4f6', borderRadius: '8px', padding: '1rem' }}>
                <h2 style={{ color: '#10b981', margin: 0 }}>{generalStats.efficiency.stock_turnover.toFixed(2)}%</h2>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '2rem', minHeight: '100vh', background: '#f9fafb' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ color: COLORS.primary, marginBottom: '0.5rem' }}>📊 Takip ve Raporlar</h1>
        <p style={{ color: '#666' }}>Detaylı raporlar, analizler ve istatistikler</p>
      </div>

      {message.text && (
        <div style={{
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1.5rem',
          background: message.type === 'success' ? '#d1fae5' : '#fee2e2',
          color: message.type === 'success' ? '#065f46' : '#991b1b'
        }}>
          {message.text}
        </div>
      )}

      {/* Filters */}
      <div style={{ background: '#fff', padding: '1.5rem', borderRadius: '12px', marginBottom: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <h3 style={{ marginTop: 0, marginBottom: '1rem' }}>🔍 Filtreler</h3>
        
        {/* Quick Date Filters */}
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <button onClick={() => setQuickDate('today')} style={quickButtonStyle}>Bugün</button>
          <button onClick={() => setQuickDate('week')} style={quickButtonStyle}>Son 7 Gün</button>
          <button onClick={() => setQuickDate('month')} style={quickButtonStyle}>Bu Ay</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Başlangıç Tarihi</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => handleFilterChange('date_from', e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Bitiş Tarihi</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => handleFilterChange('date_to', e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Proje</label>
            <select
              value={filters.project}
              onChange={(e) => handleFilterChange('project', e.target.value)}
              style={inputStyle}
            >
              <option value="">Tümü</option>
              {projects.map(p => (
                <option key={p.id} value={p.project_name}>{p.project_name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem' }}>Operatör</label>
            <input
              type="text"
              value={filters.operator}
              onChange={(e) => handleFilterChange('operator', e.target.value)}
              placeholder="Operatör adı"
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem' }}>
          <button onClick={applyFilters} style={primaryButtonStyle}>
            {loading ? '🔄 Yükleniyor...' : '🔍 Filtrele'}
          </button>
          <button onClick={clearFilters} style={secondaryButtonStyle}>
            ❌ Temizle
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', borderBottom: '2px solid #e5e7eb' }}>
        {[
          { key: 'stock', label: '📦 Stok Durumu' },
          { key: 'production', label: '🏭 Üretim Performansı' },
          { key: 'shipment', label: '🚚 Sevkiyat Analizi' },
          { key: 'general', label: '📈 Genel İstatistikler' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '1rem 1.5rem',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.key ? `3px solid ${COLORS.primary}` : 'none',
              color: activeTab === tab.key ? COLORS.primary : '#666',
              fontWeight: activeTab === tab.key ? '600' : '500',
              fontSize: '1rem',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Report Content */}
      <div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p style={{ color: '#666', fontSize: '1.1rem' }}>📊 Raporlar yükleniyor...</p>
          </div>
        ) : (
          <>
            {activeTab === 'stock' && renderStockReport()}
            {activeTab === 'production' && renderProductionReport()}
            {activeTab === 'shipment' && renderShipmentReport()}
            {activeTab === 'general' && renderGeneralStats()}
          </>
        )}
      </div>
    </div>
  );
};

// Styles
const inputStyle = {
  width: '100%',
  padding: '0.75rem',
  border: '2px solid #e5e7eb',
  borderRadius: '8px',
  fontSize: '0.95rem'
};

const primaryButtonStyle = {
  padding: '0.75rem 1.5rem',
  background: 'linear-gradient(135deg, #E40A04 0%, #B00803 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: '600',
  cursor: 'pointer',
  boxShadow: '0 4px 12px rgba(228, 10, 4, 0.3)'
};

const secondaryButtonStyle = {
  padding: '0.75rem 1.5rem',
  background: '#fff',
  color: '#666',
  border: '2px solid #e5e7eb',
  borderRadius: '8px',
  fontSize: '1rem',
  fontWeight: '600',
  cursor: 'pointer'
};

const quickButtonStyle = {
  padding: '0.5rem 1rem',
  background: '#f3f4f6',
  color: '#374151',
  border: 'none',
  borderRadius: '6px',
  fontSize: '0.9rem',
  cursor: 'pointer',
  transition: 'all 0.2s'
};

export default Reports;
